/**
 * Device reachability checker utility
 * Checks if a Fusus CORE device is reachable via cloud or local network
 */

import { DEVICE_URL_PATTERNS } from './constants';
import { logger } from './logger';

// Create component logger for DeviceCheck
const deviceCheckLogger = logger.withComponent('DeviceCheck');

/**
 * Timeout constants for device reachability checks
 */
const TIMEOUTS = {
  CLOUD_TIMEOUT_MS: 5000,
  LOCAL_TIMEOUT_MS: 3000,
} as const;

/**
 * Device connection status types
 */
export type DeviceStatus = 'cloud' | 'local' | 'unreachable';

/**
 * Result object returned by device reachability checks
 */
export interface DeviceCheckResult {
  /** Status of the device connection */
  status: DeviceStatus;
  /** URL to redirect to if device is reachable */
  url?: string;
  /** Error message if check failed */
  error?: string;
}

/**
 * Custom error class for device check failures
 */
export class DeviceCheckError extends Error {
  constructor(
    message: string,
    public readonly serial: string,
    public readonly attemptedUrls: string[]
  ) {
    super(message);
    this.name = 'DeviceCheckError';
  }
}

/**
 * Validates and sanitizes a device serial number
 * @param serial - Raw serial number input
 * @returns Sanitized serial number
 * @throws DeviceCheckError if serial is invalid
 */
function validateAndSanitizeSerial(serial: string): string {
  if (!serial || typeof serial !== 'string') {
    throw new DeviceCheckError('Serial number is required', '', []);
  }

  const sanitized = serial.trim().toLowerCase();
  
  if (sanitized.length === 0) {
    throw new DeviceCheckError('Serial number cannot be empty', serial, []);
  }

  // Basic validation - alphanumeric characters and hyphens only
  if (!/^[a-z0-9-]+$/.test(sanitized)) {
    throw new DeviceCheckError('Serial number contains invalid characters', serial, []);
  }

  return sanitized;
}

/**
 * Tests device reachability at a specific URL with timeout
 * @param url - URL to test for reachability
 * @param timeoutMs - Timeout in milliseconds
 * @returns Promise that resolves to true if device is reachable
 * @throws Never throws - returns false on any error
 */
async function testDeviceReachability(url: string, timeoutMs: number): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    await fetch(url, {
      method: 'GET',
      mode: 'no-cors',
      signal: controller.signal,
      cache: 'no-cache',
      redirect: 'follow',
      // Disable keepalive to avoid connection pooling issues
      keepalive: false,
    });
    
    clearTimeout(timeoutId);
    
    // In no-cors mode, we can't read the response body or status
    // but if the fetch completes without error, the device is likely reachable
    return true;
  } catch (error) {
    // Any error (network, timeout, abort, CORS) means unreachable
    // This is expected behavior in no-cors mode
    return false;
  }
}

/**
 * Checks if a Fusus CORE device is reachable via cloud or local network
 * 
 * This function attempts to connect to the device in the following order:
 * 1. Cloud connection (HTTPS) - Primary method
 * 2. Local network connection (HTTP) - Fallback method
 * 
 * @param serial - Device serial number (will be sanitized)
 * @returns Promise resolving to device check result
 * 
 * @example
 * ```typescript
 * const result = await checkFususDevice('ABC123');
 * if (result.status === 'cloud') {
 *   window.location.href = result.url;
 * } else if (result.status === 'unreachable') {
 *   showErrorPage();
 * }
 * ```
 */
export async function checkFususDevice(serial: string): Promise<DeviceCheckResult> {
  try {
    const sanitizedSerial = validateAndSanitizeSerial(serial);
    
    // Generate URLs for testing
    const cloudUrl = DEVICE_URL_PATTERNS.CLOUD(sanitizedSerial);
    const localUrl = DEVICE_URL_PATTERNS.LOCAL(sanitizedSerial);
    
    // First, try cloud connection (preferred)
    const cloudReachable = await testDeviceReachability(cloudUrl, TIMEOUTS.CLOUD_TIMEOUT_MS);
    if (cloudReachable) {
      return {
        status: 'cloud',
        url: DEVICE_URL_PATTERNS.CLOUD_REDIRECT(sanitizedSerial),
      };
    }

    // If cloud fails, try local connection
    const localReachable = await testDeviceReachability(localUrl, TIMEOUTS.LOCAL_TIMEOUT_MS);
    if (localReachable) {
      return {
        status: 'local',
        url: DEVICE_URL_PATTERNS.LOCAL_REDIRECT(sanitizedSerial),
      };
    }

    // Both connections failed
    return { 
      status: 'unreachable',
      error: 'Device is not reachable via cloud or local network'
    };
  } catch (error) {
    if (error instanceof DeviceCheckError) {
      return {
        status: 'unreachable',
        error: error.message,
      };
    }

    // Unexpected error - log for debugging but don't expose to user
    deviceCheckLogger.warn('Device check failed with unexpected error', error instanceof Error ? error : new Error(String(error)));
    
    return { 
      status: 'unreachable',
      error: 'An unexpected error occurred during device check'
    };
  }
}

/**
 * Convenience function to get the appropriate redirect URL for a device
 * @param serial - Device serial number
 * @returns Promise resolving to redirect URL or null if unreachable
 * 
 * @example
 * ```typescript
 * const url = await getDeviceRedirectUrl('ABC123');
 * if (url) {
 *   window.location.href = url;
 * }
 * ```
 */
export async function getDeviceRedirectUrl(serial: string): Promise<string | null> {
  const result = await checkFususDevice(serial);
  return result.url || null;
}

/**
 * Type guard to check if a device check result indicates success
 * @param result - Device check result
 * @returns True if device is reachable (cloud or local)
 */
export function isDeviceReachable(result: DeviceCheckResult): result is DeviceCheckResult & { url: string } {
  return result.status !== 'unreachable' && !!result.url;
} 